import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/*
 * `on` is consumed here instead of being passed through. styled-components v4 forwards unknown
 * props to the underlying DOM element, so a styled.span taking `on` renders `<span on="false">`
 * and React warns about a non-boolean attribute. Transient `$`-prefixed props only arrived in
 * v5, so a wrapper that destructures the prop away is the v4 answer — the interpolations below
 * still see it.
 */
// eslint-disable-next-line no-unused-vars
const Chip = ({ on, ...rest }) => <span {...rest}/>;

Chip.propTypes = {
	on: PropTypes.bool
};

/*
 * The methods, boxed together so the setting reads as one thing with parts, and so it is
 * visibly separate from the profile fields that follow it.
 */
export const MethodPanel = styled.div`
	border: 1px solid ${({ theme }) => theme['silver-darken-15']};
	border-radius: 0.25rem;
	margin-bottom: 0.75rem;
`;

export const MethodRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1.5rem;
	padding: 0.875rem 1rem;

	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme['silver-darken-15']};
	}
`;

/*
 * Name over description, so the row scans in one pass and the description cannot collide with
 * whatever follows the panel.
 */
export const MethodInfo = styled.div`
	min-width: 0;

	> strong {
		display: block;
		font-size: 0.9rem;
		font-weight: 600;
	}

	> span {
		display: block;
		margin-top: 0.15rem;
		font-size: 0.8rem;
		line-height: 1.35;
		color: ${({ theme }) => theme['silver-base']};
	}
`;

/*
 * A two-column grid with fixed widths, and the same widths on every row, so the states line up
 * in one column and the actions in another. Flexbox would size each row to its own content and
 * leave the two rows' controls a few pixels out from each other.
 */
export const MethodControls = styled.div`
	display: grid;
	/*
	 * Fixed, and generous enough for "Unused" -- the longest state either row shows. A fixed
	 * track does not grow to its content, so a tight value would clip rather than reflow, and
	 * a minmax track would let the two rows size differently and lose the alignment this is for.
	 */
	grid-template-columns: 4.75rem 6.5rem;
	align-items: center;
	gap: 0.75rem;
	flex-shrink: 0;
`;

/*
 * The action cell. One control per row -- a button or the checkbox -- centred in a box of fixed
 * width, so "Set up" and the email checkbox occupy the same footprint.
 */
export const MethodAction = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;

	button {
		width: 100%;
	}
`;

/*
 * Secondary actions live in the description rather than the action column, which holds exactly
 * one control so its fixed width stays truthful.
 */
export const InlineAction = styled.button`
	background: none;
	border: 0;
	padding: 0;
	font: inherit;
	color: ${({ theme }) => theme['blue-base']};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

/*
 * One shape for every state in here, so "on" reads the same next to a heading as it does next
 * to a method.
 */
export const StateChip = styled(Chip)`
	display: inline-block;
	/*
	 * A minimum rather than 100%: inside the method grid this fills its fixed cell so the two
	 * rows' chips are identical boxes, while beside the section heading -- where it is a flex
	 * child, not a grid cell -- 100% stretched it across the row and wrapped the heading.
	 */
	min-width: 2.75rem;
	text-align: center;
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	padding: 0.15rem 0.25rem;
	border-radius: 0.15rem;
	white-space: nowrap;
	color: ${({ theme, on }) => on ? theme['green-base'] : theme['silver-base']};
	border: 1px solid ${({ theme, on }) => on ? theme['green-base'] : theme['silver-darken-15']};
`;

export const SetupStep = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const QrPanel = styled.div`
	display: flex;
	gap: 1.25rem;
	align-items: stretch;
	flex-wrap: wrap;
`;

/*
 * Everything beside the QR code, stacked to fill its height. Laid out as a column because the
 * key, the code field and the button were previously three full-width bands stacked under the
 * QR, which left a tall empty space next to it and pushed the button off to the far right.
 */
export const SetupFields = styled.div`
	display: flex;
	flex: 1 1 16rem;
	flex-direction: column;
	justify-content: space-between;
	gap: 1rem;
	min-width: 0;
`;

export const CodeRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 0.75rem;
`;

/*
 * The QR needs a light background whatever the surrounding theme is doing: scanners read dark
 * modules on a light field, and the app is dark.
 */
export const QrFrame = styled.div`
	background: #ffffff;
	padding: 0.75rem;
	border-radius: 0.25rem;
	line-height: 0;
`;

export const ManualEntry = styled.div`
	min-width: 0;

	> label {
		display: block;
		font-size: 0.8rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 0.35rem;
	}
`;

/*
 * Monospaced and letter-spaced because this is read aloud or typed by hand when the camera will
 * not cooperate, and grouped characters are what make that survivable.
 */
export const SecretValue = styled.code`
	display: block;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 0.9rem;
	letter-spacing: 0.08em;
	word-break: break-all;
	background: ${({ theme }) => theme['midnight-base']};
	border: 1px solid ${({ theme }) => theme['silver-darken-15']};
	border-radius: 0.25rem;
	padding: 0.5rem 0.65rem;
	color: ${({ theme }) => theme['white']};
`;

/*
 * `&&` doubles the class so this actually wins. The global form styles set `width: 100%` on
 * `[type='text']`, an attribute selector of the same specificity as a styled-components class,
 * so a single class was a coin toss decided by stylesheet order -- and it lost, which is why a
 * six-digit field spanned the whole dialog.
 */
export const CodeInput = styled.input`
	&& {
		width: 8.5rem;
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: 1.1rem;
		letter-spacing: 0.25em;
		text-align: center;
	}
`;

/*
 * The codes are boxed for the same reason the methods are: ten values floating as bare text read
 * as page furniture rather than as the one thing on this screen worth acting on.
 */
export const RecoveryPanel = styled.div`
	border: 1px solid ${({ theme }) => theme['silver-darken-15']};
	border-radius: 0.25rem;
	background: ${({ theme }) => theme['midnight-base']};
	padding: 0.85rem 1rem;
`;

export const RecoveryCodeList = styled.ol`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.15rem 1rem;
	list-style: none;
	margin: 0;
	padding: 0;

	> li {
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: 0.95rem;
		letter-spacing: 0.06em;
		/* Selectable in one drag, and comfortable to read down rather than across. */
		padding: 0.15rem 0;
		user-select: all;
	}
`;

/*
 * A flex label, because a checkbox followed by text ran into its own first letter -- "I have
 * saved" read as "l have saved" with the box overlapping the I.
 */
export const AcknowledgeLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin: 0;
	font-size: 0.85rem;
	cursor: pointer;

	input {
		margin: 0;
		flex-shrink: 0;
	}
`;

/*
 * Dialog actions on their own line, right-aligned, so the buttons are not interleaved with the
 * thing the person has to read and tick first.
 */
export const DialogActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 0.5rem;
`;

export const Warning = styled.p`
	font-size: 0.85rem;
	color: ${({ theme }) => theme['yellow-base']};
	margin: 0;
	line-height: 1.4;
`;

export const ErrorText = styled.p`
	font-size: 0.85rem;
	color: ${({ theme }) => theme['red-base']};
	margin: 0;
`;

export const Actions = styled.div`
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
`;

